import { Download, FilePenLine, History } from 'lucide-react';
import { LiveCollaboratorIndicators } from '@/drafting/components/LiveCollaboratorIndicators';
import { DraftVisibilityMenu } from '@/drafting/components/DraftVisibilityStatus';
import type { DraftCollaborator } from '@/drafting/hooks/useDraftCollaboration';
import type { DraftDocumentVisibility } from '@/hooks/useDraftDocuments';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/NewButton';

export function DraftDetailToolbar({
  canShowInlineLiveStatus,
  canShowLiveCollaborators,
  canUpdateVisibility,
  downloadVersionNumber,
  isDownloading,
  isLiveEditingView,
  isUpdatingVisibility,
  liveCollaborators,
  liveStatusLabel,
  onBackToLiveEditing,
  onDownload,
  onOpenHistory,
  onVisibilityChange,
  timestampLabel,
  versionStatusLabel,
  visibility,
}: {
  canShowInlineLiveStatus: boolean;
  canShowLiveCollaborators: boolean;
  canUpdateVisibility: boolean;
  downloadVersionNumber: number | null;
  isDownloading: boolean;
  isLiveEditingView: boolean;
  isUpdatingVisibility: boolean;
  liveCollaborators: DraftCollaborator[];
  liveStatusLabel: string;
  onBackToLiveEditing: () => void;
  onDownload: () => void;
  onOpenHistory: () => void;
  onVisibilityChange: (visibility: DraftDocumentVisibility) => void;
  timestampLabel: string;
  versionStatusLabel: string;
  visibility: DraftDocumentVisibility;
}) {
  const downloadLabel = downloadVersionNumber
    ? `Download version ${downloadVersionNumber}`
    : 'Download';

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200/70 bg-white px-4 py-3">
      <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
        <span className="inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1 text-xs">
          <span
            className={cn(
              'inline-flex items-center gap-1.5 font-medium',
              isLiveEditingView ? 'text-teal-700' : 'text-amber-700',
            )}
          >
            <span
              className={cn(
                'h-1.5 w-1.5 shrink-0 rounded-full',
                isLiveEditingView ? 'bg-teal-500' : 'bg-amber-500',
              )}
            />
            {isLiveEditingView ? (
              <FilePenLine className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            ) : (
              <History className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            )}
            {canShowInlineLiveStatus ? (
              <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
                <span className="shrink-0">{liveStatusLabel}</span>
                {canShowLiveCollaborators ? (
                  <LiveCollaboratorIndicators collaborators={liveCollaborators} />
                ) : null}
              </span>
            ) : (
              versionStatusLabel
            )}
          </span>
          <span className="text-slate-300">/</span>
          <span className="whitespace-nowrap text-slate-500">
            {timestampLabel}
          </span>
        </span>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-4">
        {!isLiveEditingView ? (
          <Button
            variant="bare"
            size="sm"
            icon={FilePenLine}
            text="Back to live editing"
            className="px-1 text-teal-700 hover:text-teal-900"
            onClick={onBackToLiveEditing}
          />
        ) : null}

        <Button
          variant="bare"
          size="sm"
          icon={History}
          text="Versions"
          className="px-1 text-slate-600 hover:text-slate-950"
          onClick={onOpenHistory}
        />
        <DraftVisibilityMenu
          canUpdateVisibility={canUpdateVisibility}
          isUpdatingVisibility={isUpdatingVisibility}
          onVisibilityChange={onVisibilityChange}
          visibility={visibility}
        />
        <Button
          variant="bare"
          size="sm"
          icon={Download}
          loading={isDownloading}
          className="px-1 text-slate-600 hover:text-slate-950"
          aria-label={downloadLabel}
          tooltip={downloadLabel}
          onClick={onDownload}
        />
      </div>
    </div>
  );
}
